
import React, { useState, useEffect } from 'react';
import InvoiceTable from './InvoiceTable';
import { Invoice } from '../types';
import { api } from '../services/api';

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const data = await api.invoices.getAll();
        if (data && Array.isArray(data)) {
          setInvoices(data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, []);

  const handleUpdateInvoice = async (updatedInvoice: Invoice) => {
    setInvoices(prev => prev.map(inv => inv.id === updatedInvoice.id ? updatedInvoice : inv));
  };

  const unpaidBalance = invoices
    .filter(i => ['open', 'unpaid', 'pending', 'overdue'].includes(String(i.status).toLowerCase()))
    .reduce((sum, i) => sum + Number(i.amount || 0), 0);

  const paidMtd = invoices
    .filter(i => String(i.status).toLowerCase() === 'paid')
    .reduce((sum, i) => sum + Number(i.amount || 0), 0);

  const avgInvoice = invoices.length > 0
    ? Math.round(invoices.reduce((sum, i) => sum + Number(i.amount || 0), 0) / invoices.length)
    : 0;

  const failedCount = invoices
    .filter(i => ['failed', 'declined'].includes(String(i.status).toLowerCase()))
    .length;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Billing History</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Comprehensive log of all customer transactions and invoices.</p>
        </div>
        <div className="flex gap-3">
          <button className="px-6 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all flex items-center gap-2 active:scale-95 shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
            Export Statements
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-3xl shadow-sm">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Unpaid Balance</p>
          <p className="text-2xl font-black text-slate-900 dark:text-white">${unpaidBalance.toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-3xl shadow-sm">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Paid (MTD)</p>
          <p className="text-2xl font-black text-emerald-600">${paidMtd.toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-3xl shadow-sm">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Avg. Invoice</p>
          <p className="text-2xl font-black text-slate-900 dark:text-white">${avgInvoice.toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-3xl shadow-sm">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Failed Payments</p>
          <p className="text-2xl font-black text-red-600">{failedCount}</p>
        </div>
      </div>

      {loading && invoices.length === 0 ? (
        <div className="flex justify-center py-20">
          <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : (
        <InvoiceTable invoices={invoices} onUpdate={handleUpdateInvoice} />
      )}
    </div>
  );
};

export default Invoices;
